import api from '../api'
import type {
  ExpenseClaim,
  ExpenseClaimPayload,
  ExpenseClaimsResponse,
  ExpenseCategory,
  ExpenseItem,
  ExpensePolicy,
  ReceiptAnalysis,
} from '../../types/expenses/expenseTypes'


export const fetchExpenseClaims = async (params?: Record<string, string | number>): Promise<ExpenseClaimsResponse> => {
  const response = await api.get('/expenses/claims/', { params: { page_size: 100, ...params } })
  return response.data
}

export const createExpenseClaim = async (payload: ExpenseClaimPayload): Promise<ExpenseClaim> => {
  const response = await api.post('/expenses/claims/', payload)
  return response.data
}

export const updateExpenseClaim = async (id: string, payload: ExpenseClaimPayload): Promise<ExpenseClaim> => {
  const response = await api.patch(`/expenses/claims/${id}/`, payload)
  return response.data
}

export const deleteExpenseClaim = async (id: string): Promise<void> => {
  await api.delete(`/expenses/claims/${id}/`)
}

export const saveExpenseItem = async ({
  claimId,
  itemId,
  incurredOn,
  description,
  amount,
  category,
  receipt,
}: {
  claimId: string
  itemId?: string
  incurredOn: string
  description: string
  amount: string
  category: ExpenseCategory
  receipt?: File | null
}): Promise<ExpenseItem> => {
  const form = new FormData()
  form.append('claim', claimId)
  form.append('incurred_on', incurredOn)
  form.append('description', description)
  form.append('amount', amount)
  form.append('category', category)
  if (receipt) form.append('receipt', receipt)
  const response = itemId
    ? await api.patch(`/expenses/items/${itemId}/`, form)
    : await api.post('/expenses/items/', form)
  return response.data
}

export const deleteExpenseItem = async (id: string): Promise<void> => {
  await api.delete(`/expenses/items/${id}/`)
}

export const submitExpenseClaim = async (id: string): Promise<ExpenseClaim> => {
  const response = await api.post(`/expenses/claims/${id}/submit/`)
  return response.data
}

export const reviewExpenseClaim = async (
  id: string,
  action: 'approve' | 'reject' | 'mark-paid',
  note = '',
): Promise<ExpenseClaim> => {
  const response = await api.post(`/expenses/claims/${id}/${action}/`, { note })
  return response.data
}

export const analyzeExpenseReceipt = async (receipt: File): Promise<ReceiptAnalysis> => {
  const form = new FormData()
  form.append('receipt', receipt)
  const response = await api.post('/expenses/items/analyze-receipt/', form)
  return response.data
}

export const fetchExpenseDeclarationPdf = async (id: string): Promise<Blob> => {
  const response = await api.get(`/expenses/claims/${id}/declaration-pdf/`, { responseType: 'blob' })
  return response.data
}

export const fetchExpensePolicy = async ({
  country,
  effective = false,
}: {
  country?: string
  effective?: boolean
} = {}): Promise<ExpensePolicy> => {
  const response = await api.get('/expenses/policy/', {
    params: {
      ...(country ? { country } : {}),
      ...(effective ? { effective: 'true' } : {}),
    },
  })
  return response.data
}

export const updateExpensePolicy = async (
  policy: Pick<ExpensePolicy, 'enabled' | 'destination_email'>,
  country?: string,
): Promise<ExpensePolicy> => {
  const response = await api.put('/expenses/policy/', policy, {
    params: country ? { country } : undefined,
  })
  return response.data
}

export const resetExpensePolicy = async (country: string): Promise<ExpensePolicy> => {
  const response = await api.delete('/expenses/policy/', { params: { country } })
  return response.data
}
